/**
 * Super Admin → Maintenance.
 *
 * Destructive cleanup tools the only the super admin can run. Each
 * action shows a live preview count first and requires confirmation.
 * Endpoints are gated server-side by ``role=SUPER_ADMIN`` + canonical
 * mobile check.
 */
import { useEffect, useState } from "react";
import { Loader2, ShieldAlert, Trash2, RefreshCcw, AlertTriangle, MapPin, UserX, Tag, ShoppingCart } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function SuperMaintenance() {
    const [counts, setCounts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null); // which action key is running

    const fetchPreview = async () => {
        const { data } = await api.get("/super/maintenance/preview");
        return data;
    };

    const load = async () => {
        setLoading(true);
        try {
            const data = await fetchPreview();
            setCounts(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load maintenance preview");
        } finally { setLoading(false); }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const data = await fetchPreview();
                if (!cancelled) setCounts(data);
            } catch {
                if (!cancelled) toast.error("Failed to load maintenance preview");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const run = async (key, endpoint, confirmMsg, verb = "deleted") => {
        if (!window.confirm(confirmMsg)) return;
        setBusy(key);
        try {
            const { data } = await api.post(endpoint);
            const n = data.deleted ?? data.tagged ?? 0;
            toast.success(`${n} row(s) ${verb}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally { setBusy(null); }
    };

    const tools = [
        {
            key: "rogue-super",
            Icon: UserX,
            tone: "rose",
            title: "Purge rogue Super Admin accounts",
            desc: "Hard-delete every user row with role=SUPER_ADMIN whose mobile is NOT 9858558555. The canonical super admin is never touched.",
            count: counts?.rogue_super_admins ?? 0,
            countLabel: "rogue super admin",
            endpoint: "/super/maintenance/purge-rogue-super-admins",
            confirmMsg: `Delete ${counts?.rogue_super_admins ?? 0} rogue SUPER_ADMIN user(s)? Canonical 9858558555 is preserved.`,
        },
        {
            key: "deleted-zones",
            Icon: MapPin,
            tone: "amber",
            title: "Flush soft-deleted zones",
            desc: "Hard-delete every zone with status=DELETED. Useful to clear out legacy test zones that bloat the admin zones list.",
            count: counts?.deleted_zones ?? 0,
            countLabel: "deleted zones",
            endpoint: "/super/maintenance/purge-deleted-zones",
            confirmMsg: `Delete ${counts?.deleted_zones ?? 0} soft-deleted zone(s) permanently?`,
        },
        {
            key: "tag-impersonators",
            Icon: Tag,
            tone: "amber",
            title: "Tag legacy impersonator users",
            desc: "Back-fill is_service=true on synthetic 'impersonator-XXXX' users left over from older 'Preview as tenant' clicks. After tagging, they stop appearing as the tenant's admin in the tenant list.",
            count: counts?.untagged_impersonators ?? 0,
            countLabel: "untagged impersonators",
            endpoint: "/super/maintenance/tag-impersonators",
            confirmMsg: `Tag ${counts?.untagged_impersonators ?? 0} impersonator user(s) as service users? (Reversible — just flips a flag.)`,
            verb: "tagged",
        },
        {
            key: "orphan-carts",
            Icon: ShoppingCart,
            tone: "amber",
            title: "Clear orphan customer carts",
            desc: "Delete every cart whose restaurant_id points at a shop that has been hard-deleted. Customers with stuck carts will see an empty cart on their next visit and can re-add items. The cart auto-heal also runs at every /cart and /orders/checkout hit, but this sweep flushes every stale cart across the platform in one pass.",
            count: counts?.orphan_carts ?? 0,
            countLabel: "orphan carts",
            endpoint: "/super/maintenance/clear-orphan-carts",
            confirmMsg: `Delete ${counts?.orphan_carts ?? 0} orphan cart(s) platform-wide? Customers affected will see an empty cart on next reload.`,
        },
    ];

    const TONES = {
        rose: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", btn: "bg-rose-600" },
        amber: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", btn: "bg-amber-600" },
    };

    return (
        <div data-testid="super-maintenance-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold inline-flex items-center gap-2">
                        <ShieldAlert size={22} className="text-rose-600" /> Maintenance
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">Destructive cleanup tools. Counts refresh after each action.</p>
                </div>
                <button data-testid="maintenance-reload" onClick={load} disabled={loading} className="bg-card border border-border rounded-xl px-3 py-2 text-sm font-semibold inline-flex items-center gap-2">
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />} Refresh
                </button>
            </div>

            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900 inline-flex items-start gap-2">
                <AlertTriangle size={16} className="text-amber-700 mt-0.5 flex-shrink-0" />
                <div>
                    <p className="font-bold">These actions are permanent.</p>
                    <p className="text-xs mt-1 opacity-90">Each tool deletes rows directly from MongoDB with no undo. Read the description and the live count before running.</p>
                </div>
            </div>

            <div className="mt-6 space-y-3">
                {tools.map((t) => {
                    const tone = TONES[t.tone];
                    const disabled = busy === t.key || t.count === 0;
                    return (
                        <div key={t.key} data-testid={`maintenance-card-${t.key}`} className={`${tone.bg} border ${tone.border} rounded-2xl p-5`}>
                            <div className="flex items-start gap-4">
                                <t.Icon size={26} className={tone.text} />
                                <div className="flex-1 min-w-0">
                                    <h3 className={`font-bold ${tone.text}`}>{t.title}</h3>
                                    <p className="text-xs text-foreground/80 mt-1">{t.desc}</p>
                                    <p className={`mt-2 text-2xl font-black ${tone.text}`} data-testid={`maintenance-count-${t.key}`}>
                                        {t.count} <span className="text-xs font-semibold opacity-70">{t.countLabel}</span>
                                    </p>
                                </div>
                                <button
                                    data-testid={`maintenance-run-${t.key}`}
                                    onClick={() => run(t.key, t.endpoint, t.confirmMsg, t.verb)}
                                    disabled={disabled}
                                    className={`${tone.btn} text-white rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed`}
                                >
                                    {busy === t.key
                                        ? <Loader2 size={14} className="animate-spin" />
                                        : <Trash2 size={14} />}
                                    {t.verb === "tagged" ? "Tag rows" : "Run cleanup"}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
